/**
 * Analysis API Mock 入口
 * 集成点：AnalysisApiClient.request() 方法
 *
 * 使用方式：
 * 1. 统一配置启用：src/config/featureFlags.js -> analysisMockRuntime
 * 2. 代码切换：mockHandler.setActive(true/false)
 * 3. 场景切换：mockHandler.useScenarios(['scenarioName1', 'scenarioName2'])
 *
 * 多场景组合原则：
 * - 各功能域独立实现为 scenario（upload、history、ai、unlock 等）
 * - 按优先级组合多个场景，请求时顺序查找 handler
 * - 先匹配的场景优先响应，实现功能域叠加覆盖
 */

import { uploadAndExecuteScenario } from './scenarios/uploadAndExecute';
import { historySubjectFirstScenario } from './scenarios/historySubjectFirst';
import { analysisMockRuntime } from '@/config/featureFlags';
import { generateId, delay, getMockDelay, matchRoute } from './utils';

// 场景注册表
const scenarioRegistry = {
    upload_default_analysis: uploadAndExecuteScenario,
    history_subject_first: historySubjectFirstScenario
};

const DEFAULT_ANALYSIS_SCENARIOS = analysisMockRuntime.scenarios;

// 当前激活的场景列表（支持多场景组合，按优先级排序）
let activeScenarios = [];
let isMockActive = analysisMockRuntime.enabled;

/**
 * 构建标准化的mock错误
 */
const buildMockError = (message, status = 500, code = 'mock_error', extras = null) => {
    const error = new Error(message);
    error.status = status;
    error.code = code;
    error.isMockError = true;
    if (extras && typeof extras === 'object') {
        Object.assign(error, extras);
    }
    return error;
};

/**
 * Mock处理器主入口
 */
export const mockHandler = {
    /**
     * 全局mock开关
     */
    setActive(active) {
        isMockActive = active;
        console.log(`[Mock] ${active ? 'ENABLED' : 'DISABLED'}`);
    },

    isActive() {
        return isMockActive;
    },

    /**
     * 切换场景（单场景，保留兼容）
     * @param {string} scenarioName - 场景名称
     */
    useScenario(scenarioName) {
        return this.useScenarios([scenarioName]);
    },

    /**
     * 设置多场景组合（推荐方式）
     * @param {string[]} scenarioNames - 场景名称数组，按优先级排序
     */
    useScenarios(scenarioNames) {
        const validScenarios = [];

        for (const name of scenarioNames) {
            const scenario = scenarioRegistry[name];
            if (!scenario) {
                console.warn(`[Mock] Scenario "${name}" not found, skipping`);
                continue;
            }
            validScenarios.push({ name, scenario });
        }

        if (validScenarios.length === 0) {
            console.warn('[Mock] No valid scenarios to activate');
            return false;
        }

        activeScenarios = validScenarios;
        console.log(`[Mock] Active scenarios: ${validScenarios.map(s => s.name).join(', ')}`);

        // 调用所有场景的激活钩子
        for (const { name, scenario } of validScenarios) {
            if (typeof scenario.onActivate === 'function') {
                try {
                    scenario.onActivate();
                } catch (error) {
                    console.warn(`[Mock] onActivate hook failed for ${name}:`, error);
                }
            }
        }

        return true;
    },

    /**
     * 获取当前场景（兼容单场景模式，返回第一个）
     */
    getCurrentScenario() {
        return activeScenarios[0]?.scenario || null;
    },

    /**
     * 获取所有激活的场景
     */
    getActiveScenarios() {
        return activeScenarios.map(({ name, scenario }) => ({
            name,
            scenario,
            description: scenario.description
        }));
    },

    /**
     * 重置所有场景状态
     */
    resetScenario() {
        for (const { name, scenario } of activeScenarios) {
            if (typeof scenario.reset === 'function') {
                try {
                    scenario.reset();
                    console.log(`[Mock] Scenario reset: ${name}`);
                } catch (error) {
                    console.warn(`[Mock] Reset failed for ${name}:`, error);
                }
            }
        }
    },

    /**
     * 处理mock请求
     * @param {Object} options - 请求参数
     * @param {string} options.method - HTTP方法
     * @param {string} options.path - 请求路径
     * @param {Object} options.data - 请求体
     * @param {Object} options.params - URL参数
     * @returns {Promise<any>} - mock响应数据
     */
    async handle({ method, path, data, params, includeMeta = false }) {
        console.log('[Mock] Request:', { method, path, data, params, includeMeta });

        if (!isMockActive) {
            console.log('[Mock] Mock is not active, rejecting');
            throw buildMockError('Mock is not active', 500, 'mock_inactive');
        }

        if (activeScenarios.length === 0) {
            console.log('[Mock] No active scenario, applying runtime config scenarios');
            this.useScenarios(DEFAULT_ANALYSIS_SCENARIOS);
        }

        const routeKey = `${method} ${path}`;
        console.log('[Mock] Looking for handler:', { routeKey, activeScenarios: activeScenarios.map(s => s.name) });

        const matchedHandlers = [];

        for (const { name, scenario } of activeScenarios) {
            if (scenario.handlers?.[routeKey]) {
                matchedHandlers.push({
                    name,
                    handler: scenario.handlers[routeKey],
                    matchedParams: params,
                    scenario
                });
                continue;
            }

            for (const [pattern, routeHandler] of Object.entries(scenario.handlers || {})) {
                const patternParts = pattern.split(' ');
                if (patternParts[0] !== method) continue;

                const patternPath = patternParts.slice(1).join(' ');
                const matched = matchRoute(patternPath, path);

                if (matched) {
                    matchedHandlers.push({
                        name,
                        handler: routeHandler,
                        matchedParams: { ...params, ...matched },
                        scenario
                    });
                    break;
                }
            }
        }

        if (matchedHandlers.length === 0) {
            console.warn(`[Mock] No handler for ${routeKey}`);
            console.log('[Mock] Active scenarios:', activeScenarios.map(s => s.name));
            throw buildMockError(`No mock handler for ${routeKey}`, 404, 'mock_handler_not_found');
        }

        // 添加延迟模拟网络
        const mockDelay = getMockDelay();
        if (mockDelay > 0) {
            await delay(mockDelay);
        }

        for (const { name, handler, matchedParams, scenario } of matchedHandlers) {
            try {
                console.log('[Mock] Calling handler from scenario:', name);
                const result = await handler.call(scenario, data, {
                    params: matchedParams,
                    includeMeta
                });

                if (result === null) {
                    console.log('[Mock] Handler declined:', { routeKey, scenario: name });
                    continue;
                }

                console.log('[Mock] Response:', { method, path: path.slice(0, 50), result });
                return result;
            } catch (error) {
                if (error.isMockError) throw error;

                console.error('[Mock] Handler error:', error);
                throw buildMockError(
                    error.message,
                    error.status || 500,
                    error.code || 'mock_handler_error',
                    {
                        responseData: error.responseData || null
                    }
                );
            }
        }

        throw buildMockError(`Handler declined ${routeKey}`, 404, 'mock_handler_declined');
    },

    /**
     * 获取可用的场景列表
     */
    getAvailableScenarios() {
        return Object.entries(scenarioRegistry).map(([name, scenario]) => ({
            name,
            description: scenario.description
        }));
    },

    /**
     * 调试辅助：手动设置场景状态（设置第一个场景）
     */
    setScenarioState(key, value, scenarioName = null) {
        const targetScenario = scenarioName
            ? activeScenarios.find((item) => item.name === scenarioName)?.scenario
            : activeScenarios[0]?.scenario;

        if (!targetScenario?.state) {
            return false;
        }

        if (key && typeof key === 'object' && !Array.isArray(key)) {
            targetScenario.state = {
                ...targetScenario.state,
                ...key
            };
            return true;
        }

        if (key === 'control' && value && typeof value === 'object' && !Array.isArray(value)) {
            targetScenario.state.control = {
                ...(targetScenario.state.control || {}),
                ...value
            };
            return true;
        }

        targetScenario.state[key] = value;
        return true;
    },

    getScenarioState(key, scenarioName = null) {
        const targetScenario = scenarioName
            ? activeScenarios.find((item) => item.name === scenarioName)?.scenario
            : activeScenarios[0]?.scenario;
        return targetScenario?.state?.[key];
    },

    setScenarioControl(controlPatch = {}, scenarioName = 'upload_default_analysis') {
        if (!controlPatch || typeof controlPatch !== 'object') {
            return false;
        }
        return this.setScenarioState('control', controlPatch, scenarioName);
    },

    getScenarioControl(scenarioName = 'upload_default_analysis') {
        const control = this.getScenarioState('control', scenarioName);
        if (!control || typeof control !== 'object') {
            return null;
        }
        return { ...control };
    }
};

/**
 * runtime config 自动初始化
 */
if (analysisMockRuntime.enabled) {
    mockHandler.setActive(true);
    mockHandler.useScenarios(DEFAULT_ANALYSIS_SCENARIOS);
}

// 导出便捷工具
export { generateId, delay, matchRoute };
export { uploadAndExecuteScenario, historySubjectFirstScenario };

export default mockHandler;
